// src/components/admin/BHDedupTool.jsx
import React, { useState, useEffect } from 'react';
import { ArrowLeft, AlertTriangle, CheckCircle, Trash2, GitMerge } from 'lucide-react';
import { styles } from '../../styles/theme';
import { collection, getDocs, deleteDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebase';

const normalize = (s) => (s || '').toLowerCase().trim();

const BHDedupTool = ({ onBack }) => {
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]); // each group = array of student docs with same name
  const [merging, setMerging] = useState(null); // id of group being merged
  const [merged, setMerged] = useState([]); // ids of groups already merged
  const [dismissed, setDismissed] = useState([]); // ids of groups dismissed as not duplicates

  useEffect(() => {
    loadAndDetect();
  }, []);

  const loadAndDetect = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'students'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Group by normalized firstName + lastName + parentEmail
      const byNameAndEmail = {};
      all.forEach(s => {
        if (s.archived) return;
        const email = normalize(s.parentEmail || '');
        if (!email) return; // skip records with no parent email — can't reliably match
        const key = `${normalize(s.firstName)}_${normalize(s.lastName)}_${email}`;
        if (!byNameAndEmail[key]) byNameAndEmail[key] = [];
        byNameAndEmail[key].push(s);
      });

      // Only keep groups with 2+ students
      const duplicateGroups = Object.values(byNameAndEmail)
        .filter(g => g.length >= 2)
        .map(g => ({
          key: `${normalize(g[0].firstName)}_${normalize(g[0].lastName)}_${normalize(g[0].parentEmail)}`,
          name: `${g[0].firstName} ${g[0].lastName}`,
          email: g[0].parentEmail,
          students: g,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setGroups(duplicateGroups);
    } catch (err) {
      console.error('Error loading students:', err);
    } finally {
      setLoading(false);
    }
  };

  // Pick the "primary" record — prefer BH record with careDates, exclude records with bad student data
  const pickPrimary = (students) => {
    // Filter out records where studentFirstName/studentLastName don't match firstName/lastName
    // (indicates parent data bled into student fields during registration)
    const clean = students.filter(s => {
      if (s.studentFirstName && normalize(s.studentFirstName) !== normalize(s.firstName)) return false;
      if (s.studentLastName && normalize(s.studentLastName) !== normalize(s.lastName)) return false;
      return true;
    });

    const pool = clean.length > 0 ? clean : students; // fall back to all if none pass

    return [...pool].sort((a, b) => {
      // Prefer records with most careDates
      const aDates = (a.careDates || []).length;
      const bDates = (b.careDates || []).length;
      if (bDates !== aDates) return bDates - aDates;
      // Prefer BH billing type
      const aIsBH = a.billingType === 'bright-horizons' ? 1 : 0;
      const bIsBH = b.billingType === 'bright-horizons' ? 1 : 0;
      if (bIsBH !== aIsBH) return bIsBH - aIsBH;
      // Prefer records with careRequestNumber
      const aCR = a.careRequestNumber ? 1 : 0;
      const bCR = b.careRequestNumber ? 1 : 0;
      return bCR - aCR;
    })[0];
  };

  // Check if a group is an "obvious" duplicate — same parent email + overlapping care request numbers
  const isObviousDuplicate = (group) => {
    const emails = new Set(group.students.map(s => normalize(s.parentEmail || '')));
    if (emails.size > 1) return false; // different parent emails — needs review
    // Check if all have the same care request number or one is a subset
    const careReqs = new Set();
    group.students.forEach(s => {
      if (s.careRequestNumber) careReqs.add(s.careRequestNumber);
      (s.careRequestNumbers || []).forEach(n => careReqs.add(n));
    });
    return true; // same email = obvious duplicate regardless of care req
  };

  const handleMergeAll = async () => {
    const obvious = visibleGroups.filter(isObviousDuplicate);
    if (obvious.length === 0) return;
    if (!window.confirm(`Auto-merge ${obvious.length} obvious duplicate groups (same parent email)? This cannot be undone.`)) return;
    for (const group of obvious) {
      await handleMerge(group, true);
    }
  };

  const handleMerge = async (group, silent = false) => {
    if (!silent && !window.confirm(`Merge ${group.students.length} records for "${group.name}" into one? This cannot be undone.`)) return;
    setMerging(group.key);
    try {
      const primary = pickPrimary(group.students);
      const duplicates = group.students.filter(s => s.id !== primary.id);

      // Merge careDates from all records
      const allCareDates = [];
      const seenDates = new Set();
      [...group.students].forEach(s => {
        (s.careDates || []).forEach(cd => {
          if (!seenDates.has(cd.date)) {
            seenDates.add(cd.date);
            allCareDates.push(cd);
          }
        });
      });
      allCareDates.sort((a, b) => a.date.localeCompare(b.date));

      // Merge careRequestNumbers
      const allCareReqNumbers = new Set();
      group.students.forEach(s => {
        if (s.careRequestNumber) allCareReqNumbers.add(s.careRequestNumber);
        (s.careRequestNumbers || []).forEach(n => allCareReqNumbers.add(n));
      });

      // Build merged record — primary wins on most fields, but we enrich with data from duplicates
      const mergedData = {
        careDates: allCareDates,
        careRequestNumbers: Array.from(allCareReqNumbers),
        careRequestNumber: primary.careRequestNumber || duplicates.find(d => d.careRequestNumber)?.careRequestNumber || '',
        billingType: primary.billingType || duplicates.find(d => d.billingType)?.billingType || '',
        programSelection: primary.programSelection || duplicates.find(d => d.programSelection)?.programSelection || '',
        registrationComplete: group.students.some(s => s.registrationComplete) ? true : false,
        parentName: primary.parentName || duplicates.find(d => d.parentName)?.parentName || '',
        parentEmail: primary.parentEmail || duplicates.find(d => d.parentEmail)?.parentEmail || '',
        parentPhone: primary.parentPhone || duplicates.find(d => d.parentPhone)?.parentPhone || '',
        grade: primary.grade || duplicates.find(d => d.grade)?.grade || '',
        dateOfBirth: primary.dateOfBirth || duplicates.find(d => d.dateOfBirth)?.dateOfBirth || '',
        medicalConditions: primary.medicalConditions || duplicates.find(d => d.medicalConditions && d.medicalConditions !== 'None')?.medicalConditions || 'None',
        foodAllergies: primary.foodAllergies || duplicates.find(d => d.foodAllergies && d.foodAllergies !== 'None')?.foodAllergies || 'None',
        authorizedPickup: primary.authorizedPickup || duplicates.find(d => d.authorizedPickup)?.authorizedPickup || '',
        campStartDate: allCareDates.length > 0 ? allCareDates[0].date : primary.campStartDate || '',
        campEndDate: allCareDates.length > 0 ? allCareDates[allCareDates.length - 1].date : primary.campEndDate || '',
      };

      // Update primary record
      await updateDoc(doc(db, 'students', primary.id), mergedData);

      // Delete duplicates
      await Promise.all(duplicates.map(d => deleteDoc(doc(db, 'students', d.id))));

      setMerged(prev => [...prev, group.key]);
    } catch (err) {
      console.error('Merge error:', err);
      alert('Merge failed. Please try again.');
    } finally {
      setMerging(null);
    }
  };

  const getBillingLabel = (s) => {
    if (s.billingType === 'bright-horizons') return { label: 'BH', color: '#6d28d9', bg: '#ede9fe' };
    if (s.billingType === 'third-party') return { label: 'Third-Party', color: '#1e40af', bg: '#dbeafe' };
    return { label: 'Direct', color: '#065f46', bg: '#d1fae5' };
  };

  const visibleGroups = groups.filter(g => !merged.includes(g.key) && !dismissed.includes(g.key));
  const mergedCount = merged.length;

  return (
    <div style={{ padding: '1.5rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <button onClick={onBack} style={styles.secondaryButton}>
          <ArrowLeft size={16} /> Back to Dashboard
        </button>
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.4rem', fontWeight: 'bold', margin: 0 }}>🔀 Student Deduplication</h1>
            <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0.3rem 0 0' }}>
              Review and merge duplicate student records
            </p>
          </div>
          <button onClick={loadAndDetect} style={{ ...styles.secondaryButton, fontSize: '0.8rem' }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#6b7280' }}>Scanning for duplicates...</div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
            {[
              { label: 'Duplicate Groups Found', value: groups.length, color: '#dc2626', bg: '#fee2e2' },
              { label: 'Already Merged', value: mergedCount, color: '#10b981', bg: '#d1fae5' },
              { label: 'Remaining to Review', value: visibleGroups.length, color: '#f59e0b', bg: '#fef3c7' },
            ].map(s => (
              <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: '10px', padding: '1rem 1.25rem' }}>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: '600' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {visibleGroups.length > 0 && (
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={handleMergeAll}
                style={{ padding: '0.5rem 1.25rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '700', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                ⚡ Auto-merge all same-email groups ({visibleGroups.filter(isObviousDuplicate).length})
              </button>
            </div>
          )}

          {visibleGroups.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '4rem', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <CheckCircle size={48} style={{ color: '#10b981', margin: '0 auto 1rem' }} />
              <div style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1f2937' }}>No duplicates found</div>
              <div style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.5rem' }}>All student records are unique.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {visibleGroups.map(group => {
                const primary = pickPrimary(group.students);
                const isMerging = merging === group.key;

                return (
                  <div key={group.key} style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #fcd34d', overflow: 'hidden' }}>
                    {/* Group Header */}
                    <div style={{ padding: '0.85rem 1.25rem', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <AlertTriangle size={16} style={{ color: '#d97706' }} />
                        <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>{group.name}</span>
                        <span style={{ fontSize: '0.72rem', color: '#92400e' }}>{group.email}</span>
                        <span style={{ fontSize: '0.75rem', backgroundColor: '#fde68a', color: '#92400e', padding: '0.1rem 0.5rem', borderRadius: '4px', fontWeight: '600' }}>
                          {group.students.length} records
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setDismissed(prev => [...prev, group.key])}
                          style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#6b7280' }}
                        >
                          Not a duplicate
                        </button>
                        <button
                          onClick={() => handleMerge(group)}
                          disabled={isMerging}
                          style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem', backgroundColor: isMerging ? '#9ca3af' : '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
                        >
                          <GitMerge size={13} />
                          {isMerging ? 'Merging...' : 'Merge into 1'}
                        </button>
                      </div>
                    </div>

                    {/* Student Records Side by Side */}
                    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${group.students.length}, 1fr)`, gap: 0 }}>
                      {group.students.map((s, i) => {
                        const billing = getBillingLabel(s);
                        const isPrimary = s.id === primary.id;
                        return (
                          <div key={s.id} style={{ padding: '1rem', borderLeft: i > 0 ? '1px solid #f3f4f6' : 'none', backgroundColor: isPrimary ? '#f0fdf4' : 'white' }}>
                            {/* Record label */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                              <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '4px', backgroundColor: billing.bg, color: billing.color }}>
                                {billing.label}
                              </span>
                              {isPrimary && (
                                <span style={{ fontSize: '0.7rem', fontWeight: '700', padding: '0.15rem 0.5rem', borderRadius: '4px', backgroundColor: '#d1fae5', color: '#065f46' }}>
                                  ★ Keep this one
                                </span>
                              )}
                            </div>

                            <div style={{ fontSize: '0.78rem', display: 'flex', flexDirection: 'column', gap: '0.3rem', color: '#374151' }}>
                              <div><strong>Program:</strong> {s.programSelection || '—'}</div>
                              <div><strong>Parent:</strong> {s.parentName || '—'}</div>
                              <div><strong>Email:</strong> {s.parentEmail || '—'}</div>
                              <div><strong>Phone:</strong> {s.parentPhone || '—'}</div>
                              <div><strong>Grade:</strong> {s.grade || '—'}</div>
                              <div><strong>Care Req #:</strong> {s.careRequestNumber || '—'}</div>
                              <div><strong>Care Dates:</strong> {(s.careDates || []).length} dates</div>
                              <div><strong>Form Complete:</strong> {s.registrationComplete ? '✅ Yes' : '❌ No'}</div>
                              <div style={{ color: '#9ca3af', fontSize: '0.7rem', marginTop: '0.25rem' }}>ID: {s.id.slice(0, 12)}...</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BHDedupTool;